import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Card,
  CardBody,
  CardHeader,
  Icon,
  SimpleGrid,
  Divider,
  Flex,
  Center,
  Badge,
} from '@chakra-ui/react';
import HeaderMenus from './HeaderMenus';
import {
  FiFileText,
  FiLock,
  FiEye,
  FiXCircle,
  FiUploadCloud,
  FiTarget,
  FiStar,
  FiShield,
  FiThumbsUp,
  FiArrowDown,
} from 'react-icons/fi';

// Reusable component for feature cards
const FeatureCard = ({ icon, title, description, colorScheme }) => (
  <Card shadow="lg" textAlign="center" borderTop="4px" borderTopColor={`${colorScheme}.400`} h="full">
    <CardBody py={8}>
      <VStack spacing={4}>
        <Center w={20} h={20} bg={`${colorScheme}.100`} borderRadius="full">
          <Icon as={icon} boxSize={10} color={`${colorScheme}.600`} />
        </Center>
        <Heading size="md" color="gray.800">{title}</Heading>
        <Text color="gray.600" px={4}>{description}</Text>
      </VStack>
    </CardBody>
  </Card>
);

// Reusable component for workflow steps
const WorkflowStep = ({ icon, title, description }) => (
    <Card bg="white" shadow="xl" borderWidth="1px" borderColor="gray.200" w="full">
        <CardBody p={8}>
            <HStack spacing={8} flexDir={{ base: "column", md: "row" }} align="center">
                <Box p={6} borderRadius="2xl" bgGradient="linear(to-br, blue.400, blue.600)" color="white" shadow="lg">
                    <Icon as={icon} boxSize={16} />
                </Box>
                <VStack align="start" spacing={3}>
                    <Heading size="lg" color="blue.800">{title}</Heading>
                    <Text fontSize="lg" color="gray.700">{description}</Text>
                </VStack>
            </HStack>
        </CardBody>
    </Card>
);


const MedicalNecessityWorkflow = () => {
  return (
    <Box h="100vh" display="flex" flexDirection="column">
      <HeaderMenus />
      <Box flex="1" bg="gray.50" overflowY="auto">
        <Container maxW="7xl" py={16}>
          <VStack spacing={16} align="stretch">
            
            {/* 1. Headline Section */}
            <VStack spacing={6} textAlign="center">
              <Badge colorScheme="blue" fontSize="md" px={4} py={2} borderRadius="full">
                AI-Powered Medical Necessity
              </Badge>
              <Heading size="2xl" color="gray.800">Stop Fighting Denials. Start Building Bulletproof Claims.</Heading>
              <Text fontSize="xl" color="gray.600" maxW="4xl">
                Medical necessity is a maze of payer rules, hidden policies, and dense clinical notes. Revedy's AI gives you the clear path to payment.
              </Text>
            </VStack>

            <Divider my={8} />

            {/* 2. The Reality of Medical Necessity (The "Before") */}
            <VStack spacing={8}>
                <Heading size="xl" color="gray.700">Does This Look Familiar?</Heading>
                <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={8} w="full">
                  <FeatureCard icon={FiFileText} colorScheme="red" title="The LCD Labyrinth" description="Manually sifting through the CMS portal, trying to find the right Local Coverage Determination (LCD) and its list of covered diagnosis codes." />
                  <FeatureCard icon={FiLock} colorScheme="red" title="The Commercial Black Box" description="Logging into five different payer portals to hunt down a specific commercial policy, only to find it’s a 150-page PDF with no search function." />
                  <FeatureCard icon={FiEye} colorScheme="red" title="The Documentation Deep-Dive" description="Scouring hundreds of pages of clinical notes, hoping to spot a documented condition that matches the payer's obscure, hard-to-find rules." />
                  <FeatureCard icon={FiXCircle} colorScheme="red" title="The Inevitable Result" description="Submitting a claim that feels right, only to get a 'Not Medically Necessary' denial, forcing you to start the whole painful research process over again." />
                </SimpleGrid>
            </VStack>

            <Divider my={8} />

            {/* 3. The Revedy Transformation (The "After") */}
            <VStack spacing={8} align="stretch">
              <Heading size="xl" textAlign="center" color="gray.700">From Chaos to Clarity in Seconds</Heading>
              
              <VStack spacing={8} align="center">
                <WorkflowStep 
                    icon={FiUploadCloud}
                    title="Step 1: We Ingest the Entire Case File"
                    description="Simply provide the case. Our AI instantly consumes all relevant documents—the clinical notes, denial codes from the EOB, and identifies the specific payer, whether it's Medicare or a commercial plan like UHC."
                />
                <Icon as={FiArrowDown} boxSize={12} color="gray.300" />
                <WorkflowStep 
                    icon={FiTarget}
                    title="Step 2: We Pinpoint the Source of Truth"
                    description="Our platform automatically connects to the right policy. For Medicare, it pulls the governing LCD/NCD. For commercial payers, it searches our vast policy library to find the exact medical policy that applies. No more hunting."
                />
                <Icon as={FiArrowDown} boxSize={12} color="gray.300" />
                <WorkflowStep 
                    icon={FiStar}
                    title="Step 3: We Uncover the 'Golden' Diagnosis"
                    description="This is where Revedy changes the game. Our AI reads the clinical notes in the context of the payer's rules and frequently finds a documented diagnosis that provides perfect justification but was missed during initial coding."
                />
                <Icon as={FiArrowDown} boxSize={12} color="gray.300" />
                <WorkflowStep 
                    icon={FiShield}
                    title="Step 4: We Deliver the Bulletproof Recommendation"
                    description="You get a simple, powerful output: the optimal CPT and ICD-10 combination, an approval score, and direct quotes from the payer's own policy to back it up. Submit with total confidence."
                />
              </VStack>
            </VStack>

            <Divider my={8} />

            {/* 4. The Tangible Outcomes */}
            <VStack spacing={8}>
                <Heading size="xl" color="gray.700">The Result: Faster, Smarter, More Profitable</Heading>
                <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={8}>
                    <FeatureCard icon={FiThumbsUp} colorScheme="green" title="Eliminate Research Time" description="Automates the manual work of finding and reading payer policies." />
                    <FeatureCard icon={FiThumbsUp} colorScheme="green" title="Reduce Med-Nec Denials" description="Builds claims that are compliant with payer rules from the start." />
                    <FeatureCard icon={FiThumbsUp} colorScheme="green" title="Increase First-Pass Rate" description="Gets more claims paid correctly on the first submission." />
                    <FeatureCard icon={FiThumbsUp} colorScheme="green" title="Create Audit-Proof Records" description="Every decision is backed by documented evidence from the payer's own policies." />
                </SimpleGrid>
            </VStack>

          </VStack>
        </Container>
      </Box>
    </Box>
  );
};

export default MedicalNecessityWorkflow;